import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  AtSign,
  Bell,
  Building2,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Inbox,
  Loader2,
  MapPin,
  MessageSquareText,
  Phone,
  PhoneCall,
  Pencil,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { communicationsRepository } from './api/communications';
import type { BusinessLine, ContactDirectoryDetail, ContactDirectoryItem } from './domain';

interface ContactsPageProps {
  businesses: BusinessLine[];
  businessId: string;
  contactId: string | null;
  contactBusinessId: string | null;
  unseenNotifications: number;
  onBusinessChange: (businessId: string) => void;
  onOpenContact: (contactId: string, businessId: string, phoneE164: string) => void;
  onContactUpdated: (phoneE164: string) => void;
  onBack: () => void;
  onClose: () => void;
  onOpenSettings: () => void;
  onOpenNotifications: () => void;
  onMessage: (contact: { contactId: string; businessId: string; phoneE164: string }) => void;
  onCall: (contact: { contactId: string; businessId: string; phoneE164: string }) => void;
}

export function ContactsPage({
  businesses,
  businessId,
  contactId,
  contactBusinessId,
  unseenNotifications,
  onBusinessChange,
  onOpenContact,
  onContactUpdated,
  onBack,
  onClose,
  onOpenSettings,
  onOpenNotifications,
  onMessage,
  onCall,
}: ContactsPageProps) {
  const [contacts, setContacts] = useState<ContactDirectoryItem[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<ContactDirectoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionBusinessId, setActionBusinessId] = useState(contactBusinessId || '');
  const [contactRevision, setContactRevision] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    communicationsRepository.getContacts(businessId)
      .then(result => { if (!cancelled) setContacts(result.data); })
      .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Contacts could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId, contactRevision]);

  useEffect(() => {
    if (!contactId) {
      setDetail(null);
      setDetailError('');
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    communicationsRepository.getContact(contactId, 'all')
      .then(result => {
        if (cancelled) return;
        setDetail(result);
        const requested = contactBusinessId && result.businesses.some(line => line.businessId === contactBusinessId)
          ? contactBusinessId
          : businessId !== 'all' && result.businesses.some(line => line.businessId === businessId)
            ? businessId
            : result.originBusinessId;
        setActionBusinessId(requested || result.businesses[0]?.businessId || '');
      })
      .catch(reason => { if (!cancelled) setDetailError(reason instanceof Error ? reason.message : 'Contact details could not be loaded.'); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [contactId, contactBusinessId, businessId, contactRevision]);

  useEffect(() => setEditOpen(false), [contactId]);

  const saveContact = async (input: { displayName: string; phoneNumber: string; photoFile?: File; removePhoto: boolean }) => {
    if (!detail) return;
    await communicationsRepository.updateContact(detail.id, { displayName: input.displayName, phoneNumber: input.phoneNumber });
    if (input.photoFile) await communicationsRepository.uploadContactPhoto(detail.id, input.photoFile);
    else if (input.removePhoto) await communicationsRepository.removeContactPhoto(detail.id);
    const refreshed = await communicationsRepository.getContact(detail.id, 'all');
    setDetail(refreshed);
    onContactUpdated(refreshed.phoneE164);
    setContactRevision(value => value + 1);
    setEditOpen(false);
  };

  const visibleContacts = useMemo(() => {
    const normalized = query.replace(/\s+/g, ' ').trim().toLowerCase();
    const digits = query.replace(/\D/g, '');
    if (!normalized && !digits) return contacts;
    return contacts.filter(contact => {
      const searchable = [
        contact.displayName,
        contact.firstName,
        contact.lastName,
        contact.phoneNumber,
        contact.company,
        ...contact.businesses.flatMap(line => [line.businessName, line.displayName, line.company]),
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(normalized) || (digits.length >= 3 && contact.phoneE164.replace(/\D/g, '').includes(digits));
    });
  }, [contacts, query]);

  const selectedBusiness = businesses.find(line => line.id === businessId);
  const actionBusiness = businesses.find(line => line.id === actionBusinessId);
  const sourceLabel = detail ? identitySourceLabel(detail.identitySource) : '';

  return (
    <section className={`contacts-workspace ${contactId ? 'contact-open' : ''}`}>
      <header className="contacts-mobile-header">
        <MiniBrand />
        <strong>{contactId ? 'Contact' : selectedBusiness?.shortName || 'All contacts'}</strong>
        <HeaderUtilities unseen={unseenNotifications} onSettings={onOpenSettings} onNotifications={onOpenNotifications} />
      </header>

      <div className="contacts-page-header">
        <div>
          <button className="contacts-close" onClick={onClose}><ChevronLeft size={16} /> Communications</button>
          <p className="eyebrow">People across every line</p>
          <h1>Contacts</h1>
          <p>One dependable contact book, connected to the business where each relationship began.</p>
        </div>
        <HeaderUtilities unseen={unseenNotifications} onSettings={onOpenSettings} onNotifications={onOpenNotifications} />
      </div>

      <nav className="contact-line-filters" aria-label="Filter contacts by business">
        <button className={businessId === 'all' ? 'active' : ''} onClick={() => onBusinessChange('all')}>
          <span className="contact-filter-icon all"><Users size={16} /></span>
          <span><strong>All contacts</strong><small>{businessId === 'all' && !loading ? `${contacts.length} people` : 'Every business'}</small></span>
        </button>
        {businesses.map(business => (
          <button key={business.id} className={businessId === business.id ? 'active' : ''} onClick={() => onBusinessChange(business.id)}>
            <LineIcon business={business} />
            <span><strong>{business.shortName}</strong><small>{business.phoneNumber}</small></span>
          </button>
        ))}
      </nav>

      <div className="contacts-body">
        <section className="contacts-directory" aria-label="Contact directory">
          <div className="contacts-directory-toolbar">
            <label className="contact-search">
              <Search size={18} />
              <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search name, number, company…" aria-label="Search contacts" />
              {query && <button onClick={() => setQuery('')} aria-label="Clear contact search"><X size={16} /></button>}
            </label>
            <span>{loading ? 'Loading…' : `${visibleContacts.length} ${visibleContacts.length === 1 ? 'contact' : 'contacts'}`}</span>
          </div>
          <div className="contact-list" aria-live="polite">
            {loading && <ContactLoading />}
            {!loading && error && <ContactEmpty icon={ShieldCheck} title="Contacts unavailable" body={error} />}
            {!loading && !error && !visibleContacts.length && <ContactEmpty icon={Search} title="No contacts found" body={query ? 'Try a different name, phone number, or business.' : 'Contacts appear automatically after a call or message.'} />}
            {!loading && !error && visibleContacts.map(contact => (
              <ContactRow
                key={contact.id}
                contact={contact}
                businesses={businesses}
                active={contact.id === contactId}
                onClick={() => onOpenContact(contact.id, businessId !== 'all' ? businessId : contact.originBusinessId, contact.phoneE164)}
              />
            ))}
          </div>
        </section>

        <aside className="contact-profile-pane" aria-label="Contact card">
          {contactId && <button className="contact-mobile-back" onClick={onBack}><ChevronLeft size={18} /> Contacts</button>}
          {!contactId && <ContactNoSelection />}
          {contactId && detailLoading && <ContactProfileLoading />}
          {contactId && !detailLoading && detailError && <ContactEmpty icon={ShieldCheck} title="Contact unavailable" body={detailError} />}
          {contactId && !detailLoading && detail && (
            <ContactProfile
              detail={detail}
              businesses={businesses}
              actionBusinessId={actionBusinessId}
              actionBusiness={actionBusiness}
              sourceLabel={sourceLabel}
              onActionBusinessChange={setActionBusinessId}
              onEdit={() => setEditOpen(true)}
              onMessage={() => actionBusinessId && onMessage({ contactId: detail.id, businessId: actionBusinessId, phoneE164: detail.phoneE164 })}
              onCall={() => actionBusinessId && onCall({ contactId: detail.id, businessId: actionBusinessId, phoneE164: detail.phoneE164 })}
            />
          )}
        </aside>
      </div>
      {editOpen && detail && <ContactEditDialog detail={detail} onClose={() => setEditOpen(false)} onSave={saveContact} />}
    </section>
  );
}

function ContactRow({ contact, businesses, active, onClick }: { contact: ContactDirectoryItem; businesses: BusinessLine[]; active: boolean; onClick: () => void }) {
  const origin = businesses.find(line => line.id === contact.originBusinessId);
  return (
    <button className={`contact-row ${active ? 'active' : ''}`} onClick={onClick}>
      <ContactAvatar className="contact-row-avatar" photoUrl={contact.photoUrl} initials={contact.initials} />
      <span className="contact-row-copy">
        <strong>{contact.displayName}</strong>
        <span>{contact.phoneNumber}{contact.company ? ` · ${contact.company}` : ''}</span>
        <small>{origin ? `Originated with ${origin.name}` : 'LineWatch contact'} · {contact.interactionCount} {contact.interactionCount === 1 ? 'interaction' : 'interactions'}</small>
      </span>
      <span className="contact-row-lines" aria-label={`Associated with ${contact.businesses.length} business lines`}>
        {contact.businesses.slice(0, 4).map(association => {
          const business = businesses.find(line => line.id === association.businessId);
          return business ? <LineIcon key={business.id} business={business} compact title={business.name} /> : null;
        })}
        {contact.businesses.length > 4 && <i>+{contact.businesses.length - 4}</i>}
      </span>
      <span className="contact-row-time"><time>{relativeContactTime(contact.lastSeenAt)}</time><ChevronRight size={16} /></span>
    </button>
  );
}

function ContactProfile({ detail, businesses, actionBusinessId, actionBusiness, sourceLabel, onActionBusinessChange, onEdit, onMessage, onCall }: {
  detail: ContactDirectoryDetail;
  businesses: BusinessLine[];
  actionBusinessId: string;
  actionBusiness?: BusinessLine;
  sourceLabel: string;
  onActionBusinessChange: (businessId: string) => void;
  onEdit: () => void;
  onMessage: () => void;
  onCall: () => void;
}) {
  const profile = detail.savedLookup?.profile;
  const contactDigits = detail.phoneE164.replace(/\D/g, '').slice(-10);
  const aliases = profile?.aliases?.filter(alias => alias.replace(/\D/g, '').slice(-10) !== contactDigits) || [];
  const hasEnrichment = Boolean(profile && (
    profile.approximateAge || profile.ageRange || profile.gender || aliases.length ||
    profile.addresses.length || profile.emails.length || profile.relationships.length || profile.properties.length || profile.onlineProfiles.length
  ));
  return (
    <article className="contact-profile-card">
      <header className="contact-profile-hero">
        <ContactAvatar className="contact-profile-avatar" photoUrl={detail.photoUrl} initials={detail.initials} />
        <div>
          <p>Contact card</p>
          <h2>{detail.displayName}</h2>
          <a href={`tel:${detail.phoneE164}`}>{detail.phoneNumber}</a>
          {detail.company && <small><Building2 size={12} />{detail.company}</small>}
          <span className="contact-source"><ShieldCheck size={13} />{sourceLabel}</span>
        </div>
        <button className="contact-profile-edit-trigger" onClick={onEdit}><Pencil size={14} /><span>Edit</span></button>
      </header>

      <section className="contact-direct-actions" aria-label="Contact actions">
        <label>
          <span>Use business line</span>
          <select value={actionBusinessId} onChange={event => onActionBusinessChange(event.target.value)}>
            {detail.businesses.map(association => <option key={association.businessId} value={association.businessId}>{association.businessName}</option>)}
          </select>
        </label>
        <button onClick={onMessage} disabled={!actionBusinessId}><MessageSquareText size={19} /><span><strong>Text</strong><small>from {actionBusiness?.shortName || 'business line'}</small></span></button>
        <button onClick={onCall} disabled={!actionBusinessId}><PhoneCall size={19} /><span><strong>Call</strong><small>from {actionBusiness?.shortName || 'business line'}</small></span></button>
      </section>

      <section className="contact-profile-section">
        <div className="contact-section-heading"><div><p>Relationship</p><h3>Business history</h3></div><span>{detail.businesses.length} {detail.businesses.length === 1 ? 'line' : 'lines'}</span></div>
        <div className="contact-business-history">
          {detail.businesses.map(association => {
            const business = businesses.find(line => line.id === association.businessId);
            const originated = association.businessId === detail.originBusinessId;
            return <article key={association.businessId}>
              {business ? <LineIcon business={business} /> : <span />}
              <div><strong>{association.businessName}</strong><small>{association.displayName !== detail.displayName ? `Known here as ${association.displayName} · ` : ''}{originated ? 'Original business' : 'Also contacted'}</small></div>
              <dl>
                <div><dt>Messages</dt><dd>{association.messageCount}</dd></div>
                <div><dt>Calls</dt><dd>{association.callCount}</dd></div>
                <div><dt>Last seen</dt><dd>{formatContactDate(association.lastSeenAt)}</dd></div>
              </dl>
            </article>;
          })}
        </div>
      </section>

      <section className="contact-profile-section">
        <div className="contact-section-heading"><div><p>Activity</p><h3>Communication summary</h3></div><span>{detail.interactionCount} total</span></div>
        <div className="contact-stat-grid">
          <span><MessageSquareText size={15} /><strong>{detail.inboundMessageCount}</strong><small>texts received</small></span>
          <span><ArrowUpRight size={15} /><strong>{detail.outboundMessageCount}</strong><small>texts sent</small></span>
          <span><Phone size={15} /><strong>{detail.inboundCallCount}</strong><small>calls received</small></span>
          <span><ArrowUpRight size={15} /><strong>{detail.outboundCallCount}</strong><small>calls placed</small></span>
        </div>
        <div className="contact-date-range"><Clock3 size={14} /><span>Known since <strong>{formatContactDate(detail.firstSeenAt)}</strong></span><i /> <span>Last interaction <strong>{formatContactDate(detail.lastSeenAt)}</strong></span></div>
      </section>

      <section className="contact-profile-section">
        <div className="contact-section-heading"><div><p>Saved intelligence</p><h3>Known contact details</h3></div>{detail.savedLookup && <span>Updated {formatContactDate(detail.savedLookup.lastRefreshedAt)}</span>}</div>
        {!hasEnrichment && <div className="contact-no-enrichment"><UserRound size={20} /><div><strong>No additional saved details yet</strong><p>LineWatch will show saved lookup details here when reliable information is available.</p></div></div>}
        {hasEnrichment && profile && <div className="contact-intelligence">
          {(profile.approximateAge || profile.ageRange || profile.gender || aliases.length) && <div className="contact-fact-group"><h4>Identity</h4><dl>
            {(profile.approximateAge || profile.ageRange) && <div><dt>Age</dt><dd>{profile.ageRange ? `${profile.ageRange} (estimated)` : `About ${profile.approximateAge}`}</dd></div>}
            {profile.gender && <div><dt>Gender</dt><dd>{profile.gender}</dd></div>}
            {aliases.length ? <div><dt>Also known as</dt><dd>{aliases.join(', ')}</dd></div> : null}
          </dl></div>}
          {profile.addresses.length > 0 && <div className="contact-fact-group"><h4><MapPin size={14} /> Addresses</h4>{profile.addresses.map((address, index) => <div className="contact-address" key={`${address.formatted}-${index}`}><strong>{address.formatted}</strong><small>{address.kind === 'likely_current' ? 'Likely current' : address.kind === 'possible_prior' ? 'Possible prior' : 'Observed address'}{address.confidence ? ` · ${Math.round(address.confidence)}% confidence` : ''}</small></div>)}</div>}
          {profile.emails.length > 0 && <div className="contact-fact-group"><h4><AtSign size={14} /> Email</h4>{profile.emails.map(email => <a className="contact-email" href={`mailto:${email.email}`} key={email.email}>{email.email}</a>)}</div>}
          {profile.phones.length > 0 && <div className="contact-fact-group"><h4><Phone size={14} /> Phone intelligence</h4>{profile.phones.map(phone => <dl key={phone.numberE164}>
            {phone.cnam && <div><dt>Caller name</dt><dd>{phone.cnam}</dd></div>}
            {phone.carrier && <div><dt>Carrier</dt><dd>{phone.carrier}</dd></div>}
            {phone.lineType && <div><dt>Line type</dt><dd>{phone.lineType}</dd></div>}
            {(phone.city || phone.state) && <div><dt>Telecom location</dt><dd>{[phone.city, phone.state].filter(Boolean).join(', ')}</dd></div>}
          </dl>)}</div>}
          {profile.relationships.length > 0 && <details className="contact-more-details"><summary>Possible relatives & associates <span>{profile.relationships.length}</span></summary>{profile.relationships.map((relationship, index) => <div key={`${relationship.name}-${index}`}><strong>{relationship.name}</strong><small>{relationship.label}{relationship.city ? ` · ${relationship.city}${relationship.state ? `, ${relationship.state}` : ''}` : ''}</small></div>)}</details>}
          {profile.properties.length > 0 && <details className="contact-more-details"><summary>Property references <span>{profile.properties.length}</span></summary>{profile.properties.map((property, index) => <div key={`${property.address}-${index}`}><strong>{property.address}</strong><small>{[property.county, property.ownerName && `Owner: ${property.ownerName}`].filter(Boolean).join(' · ')}</small></div>)}</details>}
          {profile.onlineProfiles.length > 0 && <details className="contact-more-details"><summary>Public profiles <span>{profile.onlineProfiles.length}</span></summary>{profile.onlineProfiles.map(item => <a href={item.url} target="_blank" rel="noreferrer" key={item.url}>{item.label}</a>)}</details>}
          {(detail.savedLookup?.notes || detail.savedLookup?.tags.length) && <div className="contact-saved-meta">{detail.savedLookup.notes && <p>{detail.savedLookup.notes}</p>}{detail.savedLookup.tags.length > 0 && <div>{detail.savedLookup.tags.map(tag => <span key={tag}>{tag}</span>)}</div>}</div>}
        </div>}
      </section>

      <section className="contact-profile-section">
        <div className="contact-section-heading"><div><p>Timeline</p><h3>Recent interactions</h3></div><span>Latest {detail.recentInteractions.length}</span></div>
        <div className="contact-recent-list">
          {detail.recentInteractions.map(interaction => {
            const business = businesses.find(line => line.id === interaction.businessId);
            const Icon = interaction.kind === 'message' ? MessageSquareText : Phone;
            const DirectionIcon = interaction.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight;
            return <article key={`${interaction.kind}-${interaction.id}`}>
              <span className={interaction.kind}><Icon size={16} /></span>
              <div><strong>{interaction.kind === 'message' ? 'Text message' : 'Phone call'} <DirectionIcon size={12} /></strong><p>{interaction.preview}</p><small>{business?.shortName || 'Business line'} · {formatContactDateTime(interaction.occurredAt)}</small></div>
            </article>;
          })}
        </div>
      </section>
    </article>
  );
}

function ContactEditDialog({ detail, onClose, onSave }: {
  detail: ContactDirectoryDetail;
  onClose: () => void;
  onSave: (input: { displayName: string; phoneNumber: string; photoFile?: File; removePhoto: boolean }) => Promise<void>;
}) {
  const [firstName, setFirstName] = useState(detail.firstName || detail.displayName);
  const [lastName, setLastName] = useState(detail.lastName || '');
  const [phoneNumber, setPhoneNumber] = useState(detail.phoneNumber);
  const [photoFile, setPhotoFile] = useState<File | undefined>();
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(detail.photoUrl || '');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, saving]);

  useEffect(() => {
    if (!photoFile) {
      setPreviewUrl(removePhoto ? '' : detail.photoUrl || '');
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile, removePhoto, detail.photoUrl]);

  const selectPhoto = (file?: File) => {
    setError('');
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 3 * 1024 * 1024) {
      setError('Choose a PNG, JPEG, or WebP photo smaller than 3 MB.');
      return;
    }
    setPhotoFile(file);
    setRemovePhoto(false);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const displayName = `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim();
    if (displayName.length < 2) {
      setError('Enter the contact’s name.');
      return;
    }
    const digits = phoneNumber.replace(/\D/g, '');
    if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith('1')))) {
      setError('Enter a valid 10-digit US phone number.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ displayName, phoneNumber, photoFile, removePhoto });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The contact could not be saved.');
      setSaving(false);
    }
  };

  const displayName = `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim();
  const previewInitials = initialsForContact(displayName) || detail.initials;
  return <div className="contact-edit-backdrop" role="presentation" onMouseDown={event => { if (event.currentTarget === event.target && !saving) onClose(); }}>
    <section className="contact-edit-dialog" role="dialog" aria-modal="true" aria-labelledby="contact-edit-title">
      <header>
        <div><p>Contact details</p><h2 id="contact-edit-title">Edit contact</h2></div>
        <button type="button" onClick={onClose} disabled={saving} aria-label="Close contact editor"><X size={18} /></button>
      </header>
      <form onSubmit={submit}>
        <div className="contact-edit-photo">
          <ContactAvatar className="contact-edit-avatar" photoUrl={previewUrl} initials={previewInitials} />
          <div>
            <strong>Profile picture</strong>
            <span>PNG, JPEG, or WebP · up to 3 MB</span>
            <div>
              <label><Camera size={14} />{previewUrl ? 'Change photo' : 'Add photo'}<input type="file" accept="image/png,image/jpeg,image/webp" onChange={event => selectPhoto(event.target.files?.[0])} /></label>
              {previewUrl && <button type="button" onClick={() => { setPhotoFile(undefined); setRemovePhoto(true); }}><Trash2 size={13} />Remove</button>}
            </div>
          </div>
        </div>
        <div className="contact-edit-fields">
          <label><span>First name</span><input autoFocus autoComplete="given-name" maxLength={60} value={firstName} onChange={event => setFirstName(event.target.value)} /></label>
          <label><span>Last name</span><input autoComplete="family-name" maxLength={60} value={lastName} onChange={event => setLastName(event.target.value)} /></label>
          <label className="contact-edit-phone"><span>Phone number</span><input type="tel" inputMode="tel" autoComplete="tel" value={phoneNumber} onChange={event => setPhoneNumber(event.target.value)} /></label>
        </div>
        {error && <p className="contact-edit-error" role="alert">{error}</p>}
        <footer>
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" disabled={saving}>{saving ? <Loader2 className="spinning" size={16} /> : <Check size={16} />}{saving ? 'Saving…' : 'Save contact'}</button>
        </footer>
      </form>
    </section>
  </div>;
}

function ContactAvatar({ className, photoUrl, initials }: { className: string; photoUrl?: string; initials: string }) {
  return <span className={`${className} ${photoUrl ? 'has-photo' : ''}`}>{photoUrl ? <img src={photoUrl} alt="" /> : initials}</span>;
}

function initialsForContact(name: string) {
  return name.replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]?.toUpperCase()).join('');
}

function HeaderUtilities({ unseen, onSettings, onNotifications }: { unseen: number; onSettings: () => void; onNotifications: () => void }) {
  return <div className="contacts-header-utilities">
    <button className="icon-button" onClick={onSettings} aria-label="Open settings"><Settings size={18} /></button>
    <button className="icon-button notification-trigger" onClick={onNotifications} aria-label={`Notifications${unseen ? `, ${unseen} unseen` : ''}`}><Bell size={18} />{unseen > 0 && <span className="notification-count" aria-hidden="true">{unseen > 9 ? '9+' : unseen}</span>}</button>
  </div>;
}

function MiniBrand() {
  return <span className="contacts-mini-brand" aria-label="LineWatch"><i /><i /><i /></span>;
}

function LineIcon({ business, compact = false, title }: { business: BusinessLine; compact?: boolean; title?: string }) {
  return <span className={`contact-line-icon ${compact ? 'compact' : ''} ${business.logoUrl ? 'has-logo' : ''}`} style={{ color: business.color, background: business.softColor }} title={title}>{business.logoUrl ? <img src={business.logoUrl} alt="" /> : business.initials}</span>;
}

function ContactLoading() {
  return <div className="contact-loading"><Loader2 className="spinning" size={22} /><strong>Loading contacts</strong><span>Connecting names to every business line…</span></div>;
}

function ContactProfileLoading() {
  return <div className="contact-profile-loading"><Loader2 className="spinning" size={24} /><strong>Building contact card</strong><span>Gathering communication history and saved details…</span></div>;
}

function ContactEmpty({ icon: Icon, title, body }: { icon: typeof Search; title: string; body: string }) {
  return <div className="contact-empty"><Icon size={24} /><strong>{title}</strong><p>{body}</p></div>;
}

function ContactNoSelection() {
  return <div className="contact-no-selection"><span><UserRound size={28} /></span><h2>Select a contact</h2><p>Names, business relationships, communication history, and saved lookup details will appear here.</p></div>;
}

function identitySourceLabel(source: string) {
  const labels: Record<string, string> = {
    manual: 'Verified contact',
    crm: 'Business CRM',
    lookup_saved: 'Saved lookup',
    twenty_crm: 'Twenty CRM',
    assistant: 'AI intake',
    telnyx_webhook: 'Telnyx caller ID',
    telnyx_cnam: 'Telnyx CNAM',
    linewatch: 'LineWatch history',
  };
  return labels[source] || 'LineWatch contact';
}

function relativeContactTime(value: string) {
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
}

function formatContactDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: new Date(value).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' }).format(new Date(value));
}

function formatContactDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}
